import React, { useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';
import useTranslate from '../../../../Hooks/useTranslate';
import { CenteredFlexBox } from '../../../FlexBox';
import StatTile from '../../charts/StatTile';
import DonutBreakdown from '../../charts/DonutBreakdown';
import ReportCard from '../../charts/ReportCard';
import NoData from './NoData';
import { pct, outcomeLabel, resolveColor } from './reportUtils';

/**
 * MobileCallsReport — outbound calls (getCallPerformance): customers/calls KPI
 * tiles + the call-outcomes donut. See MOBILE_PLAN.md §F(9).
 */
const MobileCallsReport = function MobileCallsReport({
  callPerf = null,
  allOutcomes = [],
}) {
  const theme = useTheme();
  const intl = useIntl();
  const translate = useTranslate();
  const mode = theme.palette.mode;

  const outcomeChart = useMemo(() => {
    const sectionCalls = callPerf?.sectionCalls || [];
    const total = sectionCalls.reduce(
      (acc, c) => acc + (c.performedCalls || 0),
      0
    );
    const byOutcome = {};
    sectionCalls.forEach(({ outCome, performedCalls }) => {
      byOutcome[outCome] = (byOutcome[outCome] || 0) + (performedCalls || 0);
    });
    const items = (allOutcomes || [])
      .filter((o) => byOutcome[o.id] > 0)
      .map((o) => ({
        label: outcomeLabel(intl, o.name),
        value: pct(byOutcome[o.id], total),
        color: resolveColor(theme, o, mode),
      }))
      .sort((a, b) => b.value - a.value);
    return { total, items };
  }, [callPerf, allOutcomes]);

  return (
    <>
      <CenteredFlexBox sx={{ gap: 1.25, alignItems: 'stretch' }}>
        <StatTile
          label={translate('CUSTOMERS')}
          value={(callPerf?.performedCustomerCalls ?? 0).toLocaleString()}
          hint={translate('THIS_MONTH')}
        />
        <StatTile
          label={translate('CALLS')}
          value={outcomeChart.total.toLocaleString()}
        />
      </CenteredFlexBox>
      <ReportCard title={translate('CALL_OUTCOMES')}>
        {outcomeChart.items.length ? (
          <DonutBreakdown
            items={outcomeChart.items}
            centerValue={outcomeChart.total}
            centerLabel={translate('TOTAL')}
          />
        ) : (
          <NoData />
        )}
      </ReportCard>
    </>
  );
};

export default MobileCallsReport;
